import React, { ReactNode, useMemo, useState } from 'react';

import { FormComponent, UiComponent } from 'components';
import { usePageOption } from 'hooks';
import useModal from 'hooks/useModal';
import { StickerQuery } from 'queries';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import dayjs from 'dayjs';
import { ISticker, IStickerHolder, SearchStickerHolderFilterType } from 'types';
import * as HoldersStyle from './style';

const COLS: string[] = ['이름', '전화번호', '획득일', '지급한 어드민', '관리'];

interface Props {
  sticker: ISticker;
}

function Holders({ sticker }: Props) {
  const { handleConfirm } = useModal();
  const { pageOption, handlePage } = usePageOption(20);
  const [filter, setFilter] = useState<SearchStickerHolderFilterType>({});
  const { mutateAsync: revoke } = StickerQuery.useRevoke();

  const { data } = StickerQuery.useGetHolders(sticker.id, {
    page: pageOption.page,
    size: pageOption.size,
    ...filter,
  });

  const handleSearch = (d: SearchStickerHolderFilterType) => {
    setFilter({ name: d.name });
    handlePage({ ...pageOption, page: 1 });
  };

  const handleRevoke = (holder: IStickerHolder) => {
    handleConfirm({
      confirmContent: `'${holder.userName}' 님에게서 '${sticker.name}' 스티커를 회수하시겠습니까?`,
      yesTitle: '회수',
      confirmNext: () => {
        revoke({ id: sticker.id, userIds: [holder.userId] })
          .then(() => toast.success('회수 완료'))
          .catch(() => toast.error(DEFAULT_ERROR_MESSAGE));
      },
    });
  };

  const renderer: Record<
    (typeof COLS)[number],
    (data: IStickerHolder & { row: number }) => ReactNode
  > = useMemo(
    () => ({
      이름: ({ userName }: IStickerHolder) => <>{userName}</>,
      전화번호: ({ userPhone }: IStickerHolder) => <>{userPhone}</>,
      획득일: ({ acquiredAt }: IStickerHolder) => (
        <>{acquiredAt ? dayjs(acquiredAt).format('YYYY-MM-DD HH:mm') : '-'}</>
      ),
      '지급한 어드민': ({ grantedBy }: IStickerHolder) => (
        <>{grantedBy || '-'}</>
      ),
      관리: (holder: IStickerHolder) => (
        <UiComponent.Button
          variant="border"
          color="error"
          onClick={() => handleRevoke(holder)}
        >
          회수
        </UiComponent.Button>
      ),
    }),
    [sticker.id, sticker.name],
  );

  const rows = (data?.data || []).map((h: IStickerHolder) => ({
    ...h,
    rowKey: h.id,
  }));

  return (
    <div css={HoldersStyle.wrap}>
      <div css={HoldersStyle.head}>
        <h4>
          {sticker.name}
          <span css={{ marginLeft: 6, color: '#8B95A1', fontSize: 13 }}>
            총 {(data?.total ?? 0).toLocaleString('ko')}명
          </span>
        </h4>
        <FormComponent.Form css={HoldersStyle.searchRow} submit={handleSearch}>
          <FormComponent.SearchInput
            name="name"
            placeholder="이름으로 검색해주세요"
            css={{ width: '240px' }}
          />
        </FormComponent.Form>
      </div>
      <div css={HoldersStyle.listWrap}>
        <UiComponent.Table
          cols={COLS}
          renderer={renderer}
          data={rows}
          sticky
          empty="보유자가 없습니다"
        />
      </div>
      <UiComponent.Pagination
        pageOption={pageOption}
        handlePage={handlePage}
        totalPage={data?.totalPages || 1}
      />
    </div>
  );
}

export default Holders;
